import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import {
  getChipWindowState,
  type TopBarChipConfig,
  type TopBarChipWindowState,
} from '@pm/shared';
import { FRONTEND_CODES, FRONTEND_LABELS } from '@pm/shared/constants';
import { apiClient } from '../api/client';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { useTenantRoles } from '../api/hooks/usePermissions';
import { CHIP_COLOR_PRESETS, DEFAULT_CHIP_COLOR, chipBackground, chipTextColor } from '../lib/chipColors';
import { AppDialogTitle } from '../components/common/AppDialogTitle';

/**
 * SMSPRO-178 (Portierung nach ProjectHQ) — Verwaltung der TopBar-Chips.
 *
 * Der Zustand jedes Chips (laeuft / noch nicht / nicht mehr / dauerhaft) wird als
 * Klartext in der Liste gefuehrt. Das ist Absicht: die Zeitfenster-Regel darf
 * nicht nur im Code existieren, sonst laesst sich in der Verwaltung nicht
 * erkennen, warum ein angelegter Chip nirgends auftaucht.
 */

interface TopBarChipRow extends TopBarChipConfig {
  created_at: string;
}

const EMPTY_FORM = {
  label: '',
  color: DEFAULT_CHIP_COLOR,
  active_from: '',
  active_to: '',
  visible_roles: [] as string[],
  is_active: true,
  // ProjectHQ legt Chips standardmaessig NUR fuer PHQ an — plattformweite Chips
  // (app=null) entstehen bewusst nur ueber SMS PRO, damit ein PHQ-Hinweis nicht
  // versehentlich in SMS PRO auftaucht.
  app: 'PHQ' as string,
};

/** Farbe des Status-Chips in der Liste. */
const STATE_COLORS: Record<TopBarChipWindowState, 'success' | 'info' | 'default' | 'warning' | 'error'> = {
  permanent: 'success',
  active: 'success',
  pending: 'info',
  expired: 'default',
  disabled: 'default',
  invalid: 'error',
};

const STATE_FALLBACKS: Record<TopBarChipWindowState, string> = {
  permanent: 'Dauerhaft sichtbar',
  active: 'Sichtbar',
  pending: 'Noch nicht sichtbar',
  expired: 'Zeitraum abgelaufen',
  disabled: 'Deaktiviert',
  invalid: 'Zeitangabe ungültig',
};

/** ISO-Zeitstempel als deutsches Datum+Uhrzeit. Unbrauchbare Eingabe bleibt roh. */
function formatDateTimeDE(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 'YYYY-MM-DDTHH:mm' fuer datetime-local; leer, wenn keine Grenze gesetzt ist. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TopBarChipPage() {
  const { t } = useTranslation('common');
  const [chips, setChips] = useState<TopBarChipRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // Loeschen erst nach Rueckfrage: Ziel-Chip vormerken, ConfirmDialog benennt ihn.
  const [deleteTarget, setDeleteTarget] = useState<TopBarChipRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: rolesData } = useTenantRoles();
  const roles = rolesData?.data ?? [];

  const fetchChips = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get<TopBarChipRow[]>('/api/v1/topbar-chips');
      setChips(resp.data ?? []);
    } catch {
      // Fehlerbehandlung über globalen Error-Handler
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChips(); }, [fetchChips]);

  const handleOpenCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (chip: TopBarChipRow) => {
    setEditId(chip.id);
    setForm({
      label: chip.label,
      color: chip.color || DEFAULT_CHIP_COLOR,
      active_from: toLocalInput(chip.active_from),
      active_to: toLocalInput(chip.active_to),
      visible_roles: Array.isArray(chip.visible_roles) ? chip.visible_roles : [],
      is_active: chip.is_active,
      app: chip.app ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Leeres Feld = offene Grenze. Muss als null gehen, nicht als ''.
    const body = {
      ...form,
      active_from: form.active_from ? new Date(form.active_from).toISOString() : null,
      active_to: form.active_to ? new Date(form.active_to).toISOString() : null,
      // Leere Auswahl heisst „gilt ueberall" und muss als null gehen, nicht als ''.
      app: form.app ? form.app : null,
    };

    setSaving(true);
    try {
      if (editId) {
        await apiClient.patch(`/api/v1/topbar-chips/${editId}`, body);
      } else {
        await apiClient.post('/api/v1/topbar-chips', body);
      }
      setDialogOpen(false);
      fetchChips();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/v1/topbar-chips/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchChips();
    } finally {
      setDeleting(false);
    }
  };

  /** Rollen-Codes zu lesbaren Namen. Codes ohne Rolle bleiben als Code stehen — nie eine ID anzeigen. */
  const roleLabel = (code: string) => roles.find((r) => r.code === code)?.name ?? code;

  // Start nach Ende faellt sonst erst im Betrieb auf (Chip erscheint nie).
  const windowInvalid =
    !!form.active_from && !!form.active_to && new Date(form.active_from) > new Date(form.active_to);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            {t('topbarChips.title', 'TopBar-Chips')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('topbarChips.subtitle', 'Hinweis-Chips in der Kopfzeile anlegen, einfärben und zeitlich begrenzen.')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          {t('topbarChips.create', 'Chip anlegen')}
        </Button>
      </Box>

      {/* Die Zeitregel steht sichtbar auf der Seite, nicht nur im Code. */}
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          'topbarChips.timeHint',
          'Ohne Startzeitpunkt gilt ein Chip ab sofort, ohne Endzeitpunkt unbegrenzt. Bleiben beide Felder leer, ist der Chip dauerhaft sichtbar. Abgelaufene Chips verschwinden von selbst — sie müssen nicht abgeschaltet werden. Chips gelten nur für den aktuell gewählten Mandanten.',
        )}
      </Alert>

      {/*
        Der Umgebungs-Hinweis („TEST - Alpha") war früher fest eingebaut und galt
        für die gesamte Umgebung. Jetzt steht er als normaler Chip in dieser Liste
        — und damit pro MANDANT. Ohne diesen Hinweis nimmt man an, ein einmal
        gesetzter Umgebungshinweis erscheine automatisch überall.
      */}
      <Alert severity="warning" sx={{ mb: 2 }} data-testid="topbar-chips-tenant-scope-hint">
        {t(
          'topbarChips.envScopeHint',
          'Auch der Umgebungs-Hinweis (z. B. „TEST - Alpha") ist ein normaler Chip in dieser Liste und lässt sich bearbeiten oder löschen. Achtung: Er gilt nur für diesen Mandanten, nicht für die gesamte Umgebung. Sind auf dieser Umgebung mehrere Mandanten im Einsatz, muss der Hinweis in jedem Mandanten einzeln angelegt werden.',
        )}
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('topbarChips.fields.label', 'Chip')}</TableCell>
              <TableCell>{t('topbarChips.fields.status', 'Status')}</TableCell>
              <TableCell>{t('topbarChips.fields.activeFrom', 'Sichtbar ab')}</TableCell>
              <TableCell>{t('topbarChips.fields.activeTo', 'Sichtbar bis')}</TableCell>
              <TableCell>{t('topbarChips.fields.visibleRoles', 'Rollen')}</TableCell>
              <TableCell align="right">{t('topbarChips.fields.actions', 'Aktionen')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('topbarChips.loading', 'Wird geladen…')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : chips.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('topbarChips.noChips', 'Noch keine Chips angelegt.')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              chips.map((chip) => {
                const state = getChipWindowState(chip);
                const bg = chipBackground(chip.color);
                return (
                  <TableRow key={chip.id}>
                    <TableCell>
                      {/* Darstellung wie in der Kopfzeile — Farbwahl direkt beurteilbar. */}
                      <Chip
                        label={chip.label}
                        size="small"
                        sx={{ bgcolor: bg, color: chipTextColor(bg), fontWeight: 700, borderRadius: 0.75 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`topbarChips.states.${state}`, STATE_FALLBACKS[state])}
                        size="small"
                        color={STATE_COLORS[state]}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {chip.active_from
                        ? formatDateTimeDE(chip.active_from)
                        : <Typography variant="body2" color="text.secondary">{t('topbarChips.fromImmediately', 'ab sofort')}</Typography>}
                    </TableCell>
                    <TableCell>
                      {chip.active_to
                        ? formatDateTimeDE(chip.active_to)
                        : <Typography variant="body2" color="text.secondary">{t('topbarChips.untilForever', 'unbegrenzt')}</Typography>}
                    </TableCell>
                    <TableCell>
                      {chip.visible_roles && chip.visible_roles.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {chip.visible_roles.map((code) => (
                            <Chip key={code} label={roleLabel(code)} size="small" variant="outlined" />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('topbarChips.allRoles', 'alle Rollen')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={t('topbarChips.edit', 'Chip bearbeiten')}
                        onClick={() => handleOpenEdit(chip)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('topbarChips.delete', 'Chip löschen')}
                        onClick={() => setDeleteTarget(chip)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Anlegen/Bearbeiten */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <AppDialogTitle>
          {editId
            ? t('topbarChips.editTitle', 'Chip bearbeiten')
            : t('topbarChips.createTitle', 'Chip anlegen')}
        </AppDialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('topbarChips.fields.label', 'Chip')}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              fullWidth
              required
              slotProps={{ htmlInput: { maxLength: 60 } }}
            />

            <TextField
              label={t('topbarChips.fields.color', 'Farbe')}
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              select
              fullWidth
            >
              {CHIP_COLOR_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: preset.value }} />
                    {t(preset.labelKey, preset.fallback)}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Vorschau: zeigt sofort, ob Text auf der gewaehlten Farbe lesbar ist. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('topbarChips.preview', 'Vorschau')}
              </Typography>
              <Chip
                label={form.label || t('topbarChips.previewPlaceholder', 'Beispieltext')}
                size="small"
                sx={{
                  bgcolor: chipBackground(form.color),
                  color: chipTextColor(chipBackground(form.color)),
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  borderRadius: 0.75,
                }}
              />
            </Box>

            <TextField
              label={t('topbarChips.fields.activeFrom', 'Sichtbar ab')}
              type="datetime-local"
              value={form.active_from}
              onChange={(e) => setForm({ ...form, active_from: e.target.value })}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={t('topbarChips.fromHelp', 'Leer lassen = ab sofort sichtbar.')}
            />
            <TextField
              label={t('topbarChips.fields.activeTo', 'Sichtbar bis')}
              type="datetime-local"
              value={form.active_to}
              onChange={(e) => setForm({ ...form, active_to: e.target.value })}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={t('topbarChips.toHelp', 'Leer lassen = unbegrenzt sichtbar.')}
            />

            {windowInvalid && (
              <Alert severity="error">
                {t('topbarChips.windowInvalid', 'Der Startzeitpunkt liegt nach dem Endzeitpunkt — der Chip würde nie erscheinen.')}
              </Alert>
            )}

            <TextField
              label={t('topbarChips.fields.visibleRoles', 'Rollen')}
              value={form.visible_roles}
              onChange={(e) => {
                const value = e.target.value as unknown as string[];
                setForm({ ...form, visible_roles: typeof value === 'string' ? [value] : value });
              }}
              select
              fullWidth
              slotProps={{
                select: {
                  multiple: true,
                  // Anzeige der Rollen-NAMEN, gespeichert werden die Codes.
                  renderValue: (selected: unknown) => {
                    const codes = selected as string[];
                    if (codes.length === 0) return t('topbarChips.allRoles', 'alle Rollen');
                    return codes.map(roleLabel).join(', ');
                  },
                  displayEmpty: true,
                },
              }}
              helperText={t('topbarChips.rolesHelp', 'Keine Auswahl = für alle Rollen sichtbar.')}
            >
              {roles.map((role) => (
                <MenuItem key={role.code} value={role.code}>
                  {role.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Oberflaeche: mehrere Anwendungen teilen sich denselben Chip-Bestand.
                Ohne Auswahl erscheint der Hinweis in allen — Vorgabe hier ist PHQ. */}
            <TextField
              label={t('topbarChips.fields.app', 'Oberfläche')}
              value={form.app}
              onChange={(e) => setForm({ ...form, app: e.target.value })}
              select
              fullWidth
              helperText={t('topbarChips.appHelp', 'Keine Auswahl = in allen Oberflächen sichtbar.')}
            >
              <MenuItem value="">{t('topbarChips.allApps', 'Alle Oberflächen')}</MenuItem>
              {FRONTEND_CODES.map((code) => (
                <MenuItem key={code} value={code}>
                  {FRONTEND_LABELS[code]}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label={t('topbarChips.fields.isActive', 'Aktiv')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('topbarChips.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.label || windowInvalid || saving}>
            {t('topbarChips.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loesch-Bestaetigung */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('topbarChips.deleteTitle', 'Chip löschen?')}
        message={t(
          'topbarChips.deleteMessage',
          `Der Chip „${deleteTarget?.label ?? ''}" wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.`,
        )}
        confirmLabel={t('actions.delete')}
        cancelLabel={t('topbarChips.cancel', 'Abbrechen')}
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />
    </Box>
  );
}
